/**
 * Atlas — API client with JWT interceptors.
 * Access token stored in memory (never localStorage). Refresh token lives
 * in an httpOnly cookie set by the backend — JS cannot read or steal it.
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── In-memory access token ──
let _accessToken = null;
export const setAccessToken = (token) => { _accessToken = token; };
export const clearAccessToken = () => { _accessToken = null; };

// Silent refresh using the httpOnly cookie — bypasses api interceptors to avoid loops.
export function silentRefresh() {
  return axios.post(`${API_BASE}/api/v1/auth/refresh`, {}, { withCredentials: true });
}

const api = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // sends httpOnly refresh cookie on every request
});

// ── Request interceptor: attach in-memory access token ──
api.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// ── Response interceptor: silent refresh on 401 ──
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Endpoints whose 401s carry meaningful business semantics (wrong password,
// mfa_code_required, mfa_enrollment_required, etc.) and which MUST NOT trigger
// a silent-refresh-and-retry — otherwise the original error gets swallowed and
// replaced with a misleading "No refresh token" from /auth/refresh.
const NO_REFRESH_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/admin/register',
  '/auth/admin/verify-email',
  '/auth/admin/approve',
  '/auth/admin/reject',
  '/auth/mfa/enroll-init',
  '/auth/mfa/enroll-confirm',
];

function _shouldSkipRefresh(url) {
  if (!url) return false;
  return NO_REFRESH_PATHS.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !_shouldSkipRefresh(originalRequest.url)
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await silentRefresh();
        _accessToken = data.access_token;
        processQueue(null, data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        _accessToken = null;
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// ═══════ Auth API ═══════
// ═══════ Projects API ═══════
export const projectAPI = {
  list: () => api.get('/projects'),
  create: (data) => api.post('/projects', data),
  getBoard: (id) => api.get(`/projects/${id}/board`),
  update: (id, data) => api.patch(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  // Stages
  listStages: (id) => api.get(`/projects/${id}/stages`),
  createStage: (id, data) => api.post(`/projects/${id}/stages`, data),
  updateStage: (id, sid, data) => api.patch(`/projects/${id}/stages/${sid}`, data),
  deleteStage: (id, sid) => api.delete(`/projects/${id}/stages/${sid}`),
  reorderStages: (id, stage_ids) => api.patch(`/projects/${id}/stages/reorder`, { stage_ids }),
  // Tasks
  createTask: (id, stageId, data) => api.post(`/projects/${id}/stages/${stageId}/tasks`, data),
  updateTask: (id, tid, data) => api.patch(`/projects/${id}/tasks/${tid}`, data),
  deleteTask: (id, tid) => api.delete(`/projects/${id}/tasks/${tid}`),
  recoverTask: (id, tid) => api.post(`/projects/${id}/tasks/${tid}/recover`),
  listComments: (id, tid) => api.get(`/projects/${id}/tasks/${tid}/comments`),
  addComment: (id, tid, body) => api.post(`/projects/${id}/tasks/${tid}/comments`, { body }),
  // Members
  listMembers: (id) => api.get(`/projects/${id}/members`),
  addMember: (id, data) => api.post(`/projects/${id}/members`, data),
  updateMember: (id, mid, data) => api.patch(`/projects/${id}/members/${mid}`, data),
  removeMember: (id, mid) => api.delete(`/projects/${id}/members/${mid}`),
  // Activity
  listActivity: (id, limit = 50) => api.get(`/projects/${id}/activity`, { params: { limit } }),
  // Documents
  listDocuments: (id) => api.get(`/projects/${id}/documents`),
  // Financials
  listFinancials: (id) => api.get(`/projects/${id}/financials`),
  createFinancial: (id, data) => api.post(`/projects/${id}/financials`, data),
  updateFinancial: (id, eid, data) => api.patch(`/projects/${id}/financials/${eid}`, data),
  // Recommendations
  getRecommendations: (id, stage, country) => api.get(`/projects/${id}/recommendations`, { params: { stage, country } }),
};

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  sessionConfig: () => api.get('/auth/session-config'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  logout: () => api.post('/auth/logout'),  // backend reads+clears refresh cookie
  exportMyData: () => api.get('/auth/me/export', { responseType: 'blob' }),
  // Account deletion now requires the current password (plus an optional
  // reason string surfaced as "Help us improve" in the UI). Backend treats
  // a wrong password as 403; the UI maps that to an inline error.
  deleteMyAccount: ({ password, reason } = {}) =>
    api.delete('/auth/me', { data: { password, reason } }),

  // ── Admin onboarding (hardened flow: SECURITY_AUDIT C-4) ─────────────
  adminRegister: (data) => api.post('/auth/admin/register', data),
  adminVerifyEmail: (token) => api.post('/auth/admin/verify-email', null, { params: { token } }),
  adminApprove: (token) => api.post('/auth/admin/approve', null, { params: { token } }),
  adminReject: (token) => api.post('/auth/admin/reject', null, { params: { token } }),

  // ── MFA (TOTP) — SECURITY_AUDIT H-4 ──────────────────────────────────
  mfaEnroll: () => api.post('/auth/mfa/enroll'),
  mfaConfirm: (code) => api.post('/auth/mfa/confirm', null, { params: { code } }),
  mfaDisable: (code) => api.post('/auth/mfa/disable', null, { params: { code } }),
  // Bootstrap (used by admins who can't log in until MFA is enrolled)
  mfaEnrollInit: (email, password) => api.post('/auth/mfa/enroll-init', { email, password }),
  mfaEnrollConfirm: (enroll_token, code) =>
    api.post('/auth/mfa/enroll-confirm', null, { params: { enroll_token, code } }),
};

// ═══════ Property API ═══════
export const propertyAPI = {
  list: (page = 1) => api.get('/properties', { params: { page } }),
  get: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.patch(`/properties/${id}`, data),
  // Lifecycle — schedule a 24h pending-deletion grace, reinstate within it.
  scheduleDelete: (id, reason) => api.post(`/properties/${id}/delete`, { reason }),
  reinstate:      (id) => api.post(`/properties/${id}/reinstate`),
  // Two-phase ownership transfer.
  initiateTransfer: (id, data) => api.post(`/properties/${id}/transfer`, data),
  // Access grants
  grantAccess: (id, data) => api.post(`/properties/${id}/access`, data),
  listAccess: (id) => api.get(`/properties/${id}/access`),
  updateAccess: (id, grantId, data) => api.patch(`/properties/${id}/access/${grantId}`, data),
  revokeAccess: (id, grantId) => api.delete(`/properties/${id}/access/${grantId}`),
  getMyAccess: (id) => api.get(`/properties/${id}/my-access`),
  // Images
  getImages: (id) => api.get(`/properties/${id}/images`),
  uploadImage: (id, formData) => api.post(`/properties/${id}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteImage: (id, imageId) => api.delete(`/properties/${id}/images/${imageId}`),
  // Pro tags
  getProTags: (id) => api.get(`/properties/${id}/pro-tags`),
  addProTag: (id, data) => api.post(`/properties/${id}/pro-tags`, data),
  deleteProTag: (id, tagId) => api.delete(`/properties/${id}/pro-tags/${tagId}`),
  // Documents
  getDocuments: (id) => api.get(`/properties/${id}/documents`),
  uploadDocument: (id, formData) => api.post(`/properties/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDocument: (id, docId) => api.delete(`/properties/${id}/documents/${docId}`),
  // Financials
  getFinancials: (id) => api.get(`/properties/${id}/financials`),
  createFinancial: (id, data) => api.post(`/properties/${id}/financials`, data),
  updateFinancial: (id, entryId, data) => api.patch(`/properties/${id}/financials/${entryId}`, data),
  deleteFinancial: (id, entryId) => api.delete(`/properties/${id}/financials/${entryId}`),
  // Utilities
  getUtilities: (id) => api.get(`/properties/${id}/utilities`),
  upsertUtility: (id, type, data) => api.put(`/properties/${id}/utilities/${type}`, data),
  deleteUtility: (id, type) => api.delete(`/properties/${id}/utilities/${type}`),
};

// ═══════ Timeline API ═══════
export const timelineAPI = {
  list: (propertyId, params = {}) =>
    api.get(`/properties/${propertyId}/entries`, { params }),
  get: (propertyId, entryId) =>
    api.get(`/properties/${propertyId}/entries/${entryId}`),
  create: (propertyId, data) =>
    api.post(`/properties/${propertyId}/entries`, data),
  update: (propertyId, entryId, data) =>
    api.patch(`/properties/${propertyId}/entries/${entryId}`, data),
  delete: (propertyId, entryId) =>
    api.delete(`/properties/${propertyId}/entries/${entryId}`),
  uploadDocument: (propertyId, entryId, formData) =>
    api.post(`/properties/${propertyId}/entries/${entryId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteDocument: (propertyId, entryId, docId) =>
    api.delete(`/properties/${propertyId}/entries/${entryId}/documents/${docId}`),
};

// ═══════ Portfolio API ═══════
export const portfolioAPI = {
  getSummary: () => api.get('/portfolio/summary'),
};

// ═══════ EOI API ═══════
export const eoiAPI = {
  list: (params = {}) => api.get('/eoi', { params }),
  getMine: () => api.get('/eoi/mine'),
  getMyResponses: () => api.get('/eoi/responses'),
  get: (id) => api.get(`/eoi/${id}`),
  create: (data) => api.post('/eoi', data),
  update: (id, data) => api.patch(`/eoi/${id}`, data),
  delete: (id) => api.delete(`/eoi/${id}`),
  // Matches
  createMatch: (eoiId, data) => api.post(`/eoi/${eoiId}/matches`, data),
  listMatches: (eoiId) => api.get(`/eoi/${eoiId}/matches`),
  updateMatchStatus: (eoiId, matchId, data) => api.patch(`/eoi/${eoiId}/matches/${matchId}/status`, data),
  // Messages
  listMessages: (eoiId, matchId) => api.get(`/eoi/${eoiId}/matches/${matchId}/messages`),
  sendMessage: (eoiId, matchId, body) => api.post(`/eoi/${eoiId}/matches/${matchId}/messages`, { body }),
};

// ═══════ News / Market Insights API ═══════
export const newsAPI = {
  getNews: (category) => api.get(`/news/${category}`),
  forceRefresh: () => api.post('/news/refresh'),
  listSaved: () => api.get('/news/saved/list'),
  saveArticle: (data) => api.post('/news/saved', data),
  unsaveArticle: (url) => api.delete('/news/saved', { params: { url } }),
};

// ═══════ Marketplace API ═══════
// ═══════ Property transfers API ═══════
// Inbox + recipient verbs. Initiate lives under propertyAPI since
// it's an action on a specific property.
export const transfersAPI = {
  list:   ()   => api.get('/transfers'),
  get:    (id) => api.get(`/transfers/${id}`),
  accept: (id) => api.post(`/transfers/${id}/accept`),
  decline:(id) => api.post(`/transfers/${id}/decline`),
  cancel: (id) => api.post(`/transfers/${id}/cancel`),
};

// ═══════ Notifications API ═══════
// Phase 1 of the notification stack: durable inbox + unread badge.
// Dispatch is internal (service-layer); this exposes only read paths.
export const notificationsAPI = {
  list: (page = 1, page_size = 30) =>
    api.get('/notifications', { params: { page, page_size } }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/mark-all-read'),
};

export const marketplaceAPI = {
  searchPros: (params = {}) => api.get('/pros', { params }),
  getPro: (id) => api.get(`/pros/${id}`),
  getProDetail: (id) => api.get(`/pros/${id}/detail`),
  getMyProProfile: () => api.get('/pros/me'),
  getMyProStats: () => api.get('/pros/me/stats'),
  updateMyProfile: (data) => api.patch('/pros/me', data),
  createReview: (proId, data) => api.post(`/pros/${proId}/reviews`, data),
  updateJobVisibility: (bookingId, data) => api.patch(`/bookings/${bookingId}/visibility`, data),
  createBooking: (data) => api.post('/bookings', data),
  listBookings: () => api.get('/bookings'),
  updateBooking: (id, data) => api.patch(`/bookings/${id}`, data),
  // Invoice + payment (pro-only on the server). amount/due_date optional;
  // server defaults amount to the booking's final/quoted on issue.
  issueInvoice: (id, data = {}) => api.post(`/bookings/${id}/invoice`, data),
  markPaid:     (id, data = {}) => api.post(`/bookings/${id}/payment`, data),
  unmarkPaid:   (id)            => api.delete(`/bookings/${id}/payment`),
  listMessages: (bookingId) => api.get(`/bookings/${bookingId}/messages`),
  sendMessage: (bookingId, body) => api.post(`/bookings/${bookingId}/messages`, { body }),
  createShareLink: (data) => api.post('/share-links', data),
  viewShareLink: (token) => api.get(`/share/${token}`),
  revokeShareLink: (id) => api.delete(`/share-links/${id}`),
};

// ═══════ Admin API ═══════
export const adminAPI = {
  // Stats
  getStats: () => api.get('/admin/stats'),
  // Users
  listUsers: (params = {}) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  deactivateUser: (id) => api.delete(`/admin/users/${id}`),
  // Config
  listConfig: (category) => api.get('/admin/config', { params: category ? { category } : {} }),
  createConfig: (data) => api.post('/admin/config', data),
  updateConfig: (key, data) => api.put(`/admin/config/${key}`, data),
  deleteConfig: (key) => api.delete(`/admin/config/${key}`),
  // Plans
  listPlans: () => api.get('/admin/plans'),
  createPlan: (data) => api.post('/admin/plans', data),
  updatePlan: (id, data) => api.patch(`/admin/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/admin/plans/${id}`),
  // Audit
  listAudit: (params = {}) => api.get('/admin/audit', { params }),
  purgeAudit: (older_than_days = 365) => api.delete('/admin/audit/purge', { params: { older_than_days } }),
  // Reports
  getReports: (period = 'month') => api.get('/admin/reports', { params: { period } }),
  // Professionals
  listProfessionals: (params = {}) => api.get('/admin/professionals', { params }),
  toggleProfessionalVisibility: (proId, hidden) => api.patch(`/admin/professionals/${proId}/visibility`, { hidden }),
};
